import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import secureAxios from '@/utils/security/secureAxios';
import { AUTH_API } from '@/config/api';

const API = AUTH_API;

export default function ResetPasswordForm({ token, onSuccess }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    if (!token) {
      setError('Invalid reset token. Please request a new password reset link.');
      return;
    }

    setLoading(true);

    try {
      await secureAxios.post(
        `${API}/reset-password`,
        {
          token: token,
          new_password: formData.password
        },
        {
          timeout: 30000,
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(true);
      
      if (onSuccess) {
        onSuccess();
      } else {
        setTimeout(() => {
          navigate('/authentication?message=password_reset_success');
        }, 2000);
      }
    } catch (err) {
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (err.response) {
        const status = err.response.status;
        const data = err.response.data;
        
        if (status === 400) {
          errorMessage = data?.detail || 'Invalid or expired reset token. Please request a new password reset link.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = data?.detail || data?.message || `Error (${status}). Please try again.`;
        }
      } else if (err.request) {
        errorMessage = 'Unable to connect to server. Please check your connection and try again.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again.';
      } else {
        errorMessage = err.message || 'An unexpected error occurred.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full bg-white rounded-[14px] bg-keylime-wash px-6 py-8 text-gray-900">
        <div className="text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-green-100 p-4">
              <CheckCircle className="h-12 w-12 text-forest-ink" />
            </div>
          </div>
          <h2 className="text-2xl font-light text-forest-ink mb-4">
            Password Reset Successful
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Your password has been reset successfully. You can now log in with your new password.
          </p>
          <button
            type="button"
            onClick={() => navigate('/authentication')}
            className="w-full inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium bg-cream-paper text-forest-ink hover:bg-keylime-wash transition"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-[14px] bg-keylime-wash px-6 py-8 text-gray-900">
      <div className="mb-6">
        <h2 className="text-2xl font-light text-forest-ink mb-1">Set New Password</h2>
        <p className="text-sm text-gray-600">
          Enter your new password below. Make sure it's at least 8 characters long.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="text-sm bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        
        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            New Password
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <Lock className="h-5 w-5" />
            </span>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <p className="text-xs text-gray-500">Must be at least 8 characters</p>
        </div>

        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
            Confirm New Password
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <Lock className="h-5 w-5" />
            </span>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="w-full inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium bg-cream-paper text-forest-ink hover:bg-keylime-wash disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Resetting Password...
            </>
          ) : (
            'Reset Password'
          )}
        </button>
      </form>
    </div>
  );
}
